import React, { useEffect, useState, useCallback } from "react";
import CommonModal from "../../../components/commonModal/CommonModal";
import baseUrl from "../../../api/baseUrl";
import InputText from "../../../components/inputs/InputText";
import DropInput from "../../../components/inputs/DropInput";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import useSubmitting from "../../../hooks/useSubmitting";
import { addCustomItem } from "../../../store/pos/posSlice";

interface InstantItemProps {
  setopen: (value: boolean) => void;
  open: boolean;
  setTableData?: any;
}

interface FormData {
  branchId: string;
  martId: string;
  createdBy: string;
  updatedBy: string;
  itemId: string;
  companyId: string;
  price: string;
  description: string;
  status: string;
  quantity: string;
}

const initialFormState: FormData = {
  branchId: "",
  martId: "",
  createdBy: "",
  updatedBy: "",
  itemId: "",
  companyId: "",
  price: "",
  description: "",
  status: "added",
  quantity: "",
};

const InstantItem: React.FC<InstantItemProps> = ({
  setopen,
  open,
  setTableData,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [allProduct, setProductList] = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState(""); // for Select input value
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const dispatch = useDispatch();
  const [submitting, wrap] = useSubmitting();

  // Fetch product list for searchable dropdown
  const fetchProducts = useCallback(() => {
    baseUrl
      .get("/branch/item/get-zero-stock-items", {
        headers: {
          limit: 100000,
          page: 1,
        },
      })
      .then((res) => {
        const items = res.data.data.allItems || [];
        setProductList(items);
        setProductOptions(
          items.map((item: any) => ({
            value: item.id,
            label: item.name,
            id: item.id,
            name: item.name,
          })),
        );
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchProducts();
    setFormData(initialFormState);
  }, [fetchProducts, open]);

  // Add or select product logic
  const handleProductChange = (selectedOption: any) => {
    if (selectedOption) {
      if ((selectedOption as any).isNew) {
        setIsAddingNew(true);
        setNewProductName(inputValue);
        setFormData((prev) => ({
          ...prev,
          itemId: "",
          price: "",
          quantity: "",
          description: "",
        }));
      } else {
        setIsAddingNew(false);
        setNewProductName("");
        setFormData((prev) => ({
          ...prev,
          itemId: selectedOption.value,
          price: "",
          quantity: "",
          description: "",
        }));
      }
    } else {
      setIsAddingNew(false);
      setNewProductName("");
      setFormData((prev) => ({
        ...prev,
        itemId: "",
        price: "",
        quantity: "",
        description: "",
      }));
    }
  };

  // Unified submit handler for both new and existing items
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isAddingNew) {
      // Validate new product fields
      if (!newProductName || !formData.price || !formData.quantity) {
        toast.error("Please enter name, price and quantity");
        return;
      }
      try {
        const res = await baseUrl.post("/branch/item/create-instant-item", {
          name: newProductName,
          price: Number(formData.price),
          stock: Number(formData.quantity),
          status: "Active",
        });
        const newItem = res.data?.data;
        if (newItem) {
          toast.success("Product added successfully");
          setProductList((prev) => [...prev, newItem]);
          setProductOptions((prev) => [
            ...prev,
            {
              value: newItem.id,
              label: newItem.name,
              id: newItem.id,
              name: newItem.name,
            },
          ]);
          // Add to POS
          const payload = {
            ...newItem,
            ...formData,
            itemId: newItem.id,
            price: Number(formData.price),
            quantity: Number(formData.quantity),
            description: formData.description || "",
            status: "added",
          };
          dispatch(addCustomItem(payload));
          setopen(false);
          setFormData(initialFormState);
          setIsAddingNew(false);
          setNewProductName("");
        }
      } catch (err) {
        console.log(err);

        toast.error("Failed to add product");
      }
      return;
    }

    // Existing product logic
    const selectedProduct =
      allProduct.find(
        (item: any) => String(item.id) === String(formData.itemId),
      ) || {};

    const payload = {
      ...selectedProduct,
      ...formData,
      branchId: formData.branchId || "1",
      martId: formData.martId || "1",
      createdBy: formData.createdBy || "1",
      updatedBy: formData.updatedBy || "1",
      companyId: formData.companyId || "1",
      status: formData.status || "added",
      quantity: parseInt(formData.quantity, 10) || 1,
      price: parseInt(formData.price, 10) || selectedProduct.price || 0,
      description: formData.description || selectedProduct.description || "",
    };

    dispatch(addCustomItem(payload));
    setopen(false);
    setFormData(initialFormState);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onCancel = () => {
    setFormData(initialFormState);
  };

  return (
    <CommonModal
      onSubmit={wrap(handleSubmit)}
      setopen={setopen}
      open={open}
      title="Add Instant Item"
      submit="Submit"
      onCancel={onCancel}
      submitting={submitting}
      submittingText="Submitting..."
    >
      <div
        style={{ display: "flex", flexDirection: "column", padding: "10px" }}
      >
        <Select
          id="dropdown"
          options={
            inputValue &&
            !productOptions.some(
              (opt) => opt.label.toLowerCase() === inputValue.toLowerCase(),
            )
              ? [
                  ...productOptions,
                  {
                    value: "__add_new__",
                    label: `Add new product: "${inputValue}"`,
                    isNew: true,
                  },
                ]
              : productOptions
          }
          onInputChange={(val) => setInputValue(val)}
          onChange={handleProductChange}
          classNamePrefix="react-select"
          name="itemId"
          value={
            isAddingNew
              ? {
                  value: "__add_new__",
                  label: `Add new product: "${newProductName || inputValue}"`,
                  isNew: true,
                }
              : productOptions.find((opt) => opt.value === formData.itemId) ||
                null
          }
          placeholder="Select or search product..."
          isClearable
        />
        {isAddingNew && (
          <InputText
            name="newProductName"
            label="Product Name"
            value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)}
            required
          />
        )}
        <InputText
          name="quantity"
          label="Quantity"
          type="number"
          required
          value={formData.quantity}
          onChange={handleChange}
        />
        {(isAddingNew || formData.itemId) && (
          <InputText
            name="price"
            label="Unit Price"
            type="number"
            required={isAddingNew}
            value={formData.price}
            onChange={handleChange}
          />
        )}
        <InputText
          name="description"
          label="Description"
          value={formData.description}
          onChange={handleChange}
        />
      </div>
    </CommonModal>
  );
};

export default InstantItem;
